import { z } from 'zod';
import { Description, ExampleCompliance, Severity, Title } from '@/rule';
import { jsonToPage, type Page, pageSchemaOf } from '@/generic/pagination';
import { Optional } from '@/generic/functional';
import {
  DraftExample,
  DraftRule,
  type DraftRuleEdit,
} from '@/rules-management/domain/DraftRule.ts';

const draftSeveritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);

export type DraftSeverityJson = z.infer<typeof draftSeveritySchema>;

const draftComplianceSchema = z.enum(['COMPLIANT', 'NON_COMPLIANT']);

type DraftComplianceJson = z.infer<typeof draftComplianceSchema>;

const draftExampleSchema = z.object({
  description: z.string().nullish(),
  snippet: z.string().nullish(),
  compliance: draftComplianceSchema.nullish(),
});

type DraftExampleJson = z.infer<typeof draftExampleSchema>;

const draftRuleSchema = z.object({
  id: z.string(),
  title: z.string().nullish(),
  description: z.string().nullish(),
  severity: draftSeveritySchema.nullish(),
  examples: z.array(draftExampleSchema),
});

export type DraftRuleJson = z.infer<typeof draftRuleSchema>;

export const draftRulesPageSchema = pageSchemaOf(draftRuleSchema);

export type DraftRulesJson = z.infer<typeof draftRulesPageSchema>;

const jsonToSeverity = (severity: DraftSeverityJson): Severity => {
  switch (severity) {
    case 'CRITICAL':
      return Severity.CRITICAL;
    case 'HIGH':
      return Severity.HIGH;
    case 'MEDIUM':
      return Severity.MEDIUM;
    case 'LOW':
      return Severity.LOW;
  }
};

const jsonToCompliance = (compliance: DraftComplianceJson): ExampleCompliance => {
  switch (compliance) {
    case 'COMPLIANT':
      return ExampleCompliance.COMPLIANT;
    case 'NON_COMPLIANT':
      return ExampleCompliance.NON_COMPLIANT;
  }
};

const jsonToDraftExample = (json: DraftExampleJson): DraftExample =>
  DraftExample.of({
    description: Optional.ofFalsifiable(json.description),
    snippet: Optional.ofFalsifiable(json.snippet),
    compliance: Optional.ofFalsifiable(json.compliance).map(jsonToCompliance),
  });

const jsonToDraftRule = (json: DraftRuleJson): DraftRule =>
  DraftRule.of({
    id: json.id,
    title: Optional.ofFalsifiable(json.title).map((title) => Title.of(title)),
    description: Optional.ofFalsifiable(json.description).map((description) =>
      Description.of(description),
    ),
    severity: Optional.ofFalsifiable(json.severity).map(jsonToSeverity),
    examples: json.examples.map(jsonToDraftExample),
  });

export const parseJsonToDraftRulesPage = (json: unknown): Page<DraftRule> =>
  jsonToPage(draftRulesPageSchema.parse(json), jsonToDraftRule);

export const parseJsonToDraftRule = (json: unknown): DraftRule =>
  jsonToDraftRule(draftRuleSchema.parse(json));

const severityToJson = (severity: Severity): DraftSeverityJson => {
  switch (severity) {
    case Severity.CRITICAL:
      return 'CRITICAL';
    case Severity.HIGH:
      return 'HIGH';
    case Severity.MEDIUM:
      return 'MEDIUM';
    case Severity.LOW:
      return 'LOW';
    case Severity.INFO:
      throw new Error('A draft rule cannot use the info severity');
  }
};

const complianceToJson = (compliance: ExampleCompliance): DraftComplianceJson => {
  switch (compliance) {
    case ExampleCompliance.COMPLIANT:
      return 'COMPLIANT';
    case ExampleCompliance.NON_COMPLIANT:
      return 'NON_COMPLIANT';
  }
};

const draftExampleToJson = (example: DraftExample) => ({
  description: example.description.orUndefined(),
  snippet: example.snippet.orUndefined(),
  compliance: example.compliance.map(complianceToJson).orUndefined(),
});

export const draftRuleEditToJson = (edit: DraftRuleEdit) => ({
  title: edit.title.orUndefined(),
  description: edit.description.orUndefined(),
  severity: edit.severity.map(severityToJson).orUndefined(),
  examples: edit.examples.map(draftExampleToJson),
});
